import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Col, Div, ExpanderColumn, Icon, Row, Text } from '@pinpoint/design-kit';

import { checkAllTablesRowsExpanded, checkHiddenByRole, valFromQs } from 'src/functions';
import { Page } from 'src/containers';
import { appActions, modelsListActions } from 'src/store/actions';
import { ApproveModelDialog, DeleteModelDialog, HideModelDialog } from 'src/components/dialogs';
import {
  ActuarialCellActions,
  CellActions,
  CellDate,
  CellHit,
  CellHitMatched,
  CellMiss,
  CellMissMatched,
  CellNumber,
  CellPercent,
  CellStatus,
  CellTag,
  CellText,
  CellValidation,
  CellValidationMatched,
} from 'src/components/models/cells';
import { ROUTES } from 'src/config';
import CONSTANTS from 'src/store/constants';
import { Button, Message, StatusFilterButtonGroup } from 'src/components';
import { ViewModelFeatureToggle } from './feature_toggles';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const noClaimsAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const noClaims = Number(model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_zero_claims ?? 0);
  return Number.isFinite(noClaims) ? noClaims : null;
};
const someClaimsAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const someClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_gte_one_claims ?? 0
  );
  return Number.isFinite(someClaims) ? someClaims : null;
};

const expectedRiskAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const riskPerc = Number(model?.modelData?.dataPayload?.risk_percentage);
  return Number.isFinite(riskPerc) ? riskPerc : '–';
};

const onboardedRiskAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const onbSomeClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.onb_gte_one_claims ?? 0
  );
  const onbZeroClaims = Number(model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.onb_zero_claims ?? 0);
  return [onbSomeClaims, onbZeroClaims].every((val) => Number.isFinite(val))
    ? (100 * onbSomeClaims) / (onbSomeClaims + onbZeroClaims)
    : 0;
};

const matchedRiskAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const matchSomeClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_gte_one_claims ?? 0
  );
  const matchZeroClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_zero_claims ?? 0
  );
  return [matchSomeClaims, matchZeroClaims].every((v) => Number.isFinite(v))
    ? (100 * matchSomeClaims) / (matchSomeClaims + matchZeroClaims)
    : 0;
};

const matchedValAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const clusters = model?.modelData?.dataPayload?.actuarialSegment?.clusters ?? [];
  const validationCluster = clusters.find((info) => info?.tag === 'ACTVAL');
  return validationCluster?.matched ?? 0;
};
const matchValPercAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const clusters = model?.modelData?.dataPayload?.actuarialSegment?.clusters ?? [];
  const segmentMatchedUsers = model?.modelData?.dataPayload?.actuarialSegment?.matched;
  const validationCluster = clusters.find((info) => info?.tag === 'ACTVAL');
  const validationMatchedUsers = validationCluster?.matched;
  if (segmentMatchedUsers && validationMatchedUsers) {
    const matchedPerc = validationMatchedUsers / segmentMatchedUsers;
    if (Number.isFinite(matchedPerc)) return matchedPerc * 100;
  }
  return 0;
};

const actActionsAccessor = (model) => {
  const { dataModelType, recentStatus, id, hiddenByRole, modelData } = model;
  const metadata = modelData?.metadataPayload ?? {};
  const validation = (modelData?.dataPayload?.actuarialSegment?.clusters ?? []).find((cluster) => {
    const { name } = cluster;
    return !!name?.includes?.('_validation');
  });
  return JSON.stringify({
    id,
    modelData,
    dataModelType,
    hiddenByRole,
    metadata,
    recentStatus,
    validation,
  });
};

const statusColW = '4rem';
const dtColW = '6rem';
const actionColW = '3rem';
const tagColW = '8rem';
const actTblCtrlW = '2rem 1rem';
const nameColW = 'minmax(10rem, 16rem)';

const actuarialOverheaderWidths = `${actTblCtrlW} ${nameColW} 4fr 3fr 2fr ${tagColW} ${actionColW} ${dtColW} ${statusColW}`;
const actuarialColWidths = `${actTblCtrlW} ${nameColW} 2fr 2fr 1fr 1fr 1fr 1fr 1fr ${tagColW} ${actionColW} ${dtColW} ${statusColW}`;
const classifierColWidths = `3rem ${nameColW} 1fr .5fr 1fr .5fr 1fr 0.5fr 0.5fr ${tagColW} ${actionColW} ${dtColW} ${statusColW}`;

function Models({
  history,
  app: {
    profile: { email, role, siteRole, tenant, originalTenant },
  },
  modelsList: {
    models,
    fetchModelsStatus,
    selectedModelIds,
    gridState,
    controlledGridState,
    maxGridRowSelection,
    selectedGridRows,
    approveModelStatus,
    downloadUrl,
    fetchModelDownloadStatus,
    checkDeleteModelDependents,
  },
  fetchModels,
  selectGridRow,
  deselectGridRow,
  setGridState,
  editModel,
  showModel,
  hideModel,
  checkDeleteModel,
  deleteModel,
  approveModel,
  enqueueNotification,
  deselectAllModelIds,
  fetchClientByTenant,
  fetchEntityDl,
  unset,
}) {
  const search = new URLSearchParams(history.location.search);
  const modelListFlat = useMemo(() => {
    const out = [];
    if (models)
      models.forEach((model) => {
        out.push({ id: model.id, hiddenByRole: model.hiddenByRole, dataModelType: model?.dataModelType });
        model.modelGroup.forEach((subModel) => out.push({ id: subModel.id, hiddenByRole: subModel.hiddenByRole }));
      });
    return out;
  }, [models]);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState([CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.ADMIN].some((r) => r === role));
  const [modelToApproveId, setModelToApproveId] = useState(null);
  const [modelToDeleteId, setModelToDeleteId] = useState(null);
  const [modelToDeleteType, setModelToDeleteType] = useState(null);
  const [modelToHideId, setModelToHideId] = useState(null);
  const [disableModelResults, setDisableModelResults] = useState(false);
  const [disableInputDistributions, setDisableInputDistributions] = useState(false);
  const [rowsOpen, setRowsOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(valFromQs(search, 'filter', CONSTANTS.TAL_MODEL.FILTERS, ''));
  const [selectedClass, setSelectedClass] = useState(
    valFromQs(search, 'modelClass', CONSTANTS.TAL_MODEL.MODEL_CLASSES, '')
  );

  const disabledInputDistStr = useMemo(
    () =>
      disableInputDistributions ? "Select an Actuarial model's parent row" : 'View distributions of loss outcomes',
    [disableInputDistributions]
  );
  const disabledLiftChartsStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View lift charts for selected model(s)'),
    [disableModelResults]
  );
  const disabledValidationAnalyticsStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View validation set analytics'),
    [disableModelResults]
  );
  const disabledRoiCalculatorStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View financial impact model'),
    [disableModelResults]
  );

  const selectedModelClasses = useMemo(() => {
    if (selectedModelIds.length > 0) {
      return selectedModelIds.map((modelId) =>
        modelListFlat.find((model) => model.id === modelId)?.dataModelType === 'CLASSIFIER' ? 'classifier' : 'actuarial'
      );
    }
    return [];
  }, [selectedModelIds, modelListFlat]);
  const handleDeleteModel = useCallback(() => deleteModel(modelToDeleteId), [modelToDeleteId, deleteModel]);
  const handleHideModel = useCallback(() => {
    const model = modelListFlat.find((elem) => elem.id === modelToHideId);
    const hidden = model.hiddenByRole;
    if (hidden) showModel(modelToHideId);
    else hideModel(modelToHideId);
  }, [modelToHideId, hideModel, modelListFlat, showModel]);
  const handleApproveModel = useCallback(() => approveModel(modelToApproveId), [modelToApproveId, approveModel]);
  const validModels = useCallback(
    (idList) => {
      const filteredModels = models.filter(({ id }) => idList.includes(id));
      return filteredModels.every(
        ({ recentStatus, dataModelType }) =>
          recentStatus === CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS &&
          (dataModelType.includes('ACT_') || dataModelType === 'CLASSIFIER')
      );
    },
    [models]
  );
  const getStatusGroup = useCallback(
    (status) => CONSTANTS.TAL_MODEL.STATE_GROUPINGS[status] || 0 /* unknown status */,
    []
  );
  const getData = useCallback(() => {
    const filter = {
      filters: [],
      order_by: [{ field: 'created_at', direction: 'desc' }],
      fields:
        'items{id,name,hiddenByRole,typeDetail,predictionType,modelData{metadataPayload,dataPayload{actuarial_segment_id,positive_segment_id,negative_segment_id,validation_segment_id,pets_percentage,risk_percentage}},recentStatus,createdAt,dataModelType,modelGroup{id,name,hiddenByRole,createdAt,predictionType,dataModelType,typeDetail,recentStatus,modelData{metadataPayload,dataPayload{actuarial_segment_id,positive_segment_id,negative_segment_id,validation_segment_id}}}},*',
    };
    switch (selectedCode) {
      case CONSTANTS.TAL_MODEL.FILTER.PENDING_APPROVAL: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [CONSTANTS.TAL_MODEL.STATE.PENDING_APPROVAL],
        });
        break;
      }
      case CONSTANTS.TAL_MODEL.FILTER.IN_PROGRESS: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [
            CONSTANTS.TAL_MODEL.STATE.APPROVED,
            CONSTANTS.TAL_MODEL.STATE.MODEL_GENERATION_INITIATED,
            CONSTANTS.TAL_MODEL.STATE.MODEL_GENERATION_SUCCESS,
            CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_INITIATED,
          ],
        });
        break;
      }
      case CONSTANTS.TAL_MODEL.FILTER.ERRORED: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [
            CONSTANTS.TAL_MODEL.STATE.MODEL_GENERATION_FAILURE,
            CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_FAILURE,
          ],
        });
        break;
      }
      case CONSTANTS.TAL_MODEL.FILTER.COMPLETED: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS],
        });
        break;
      }
      default:
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: CONSTANTS.TAL_MODEL.STATES,
        });
        break;
    }
    switch (selectedClass) {
      case CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER: {
        filter.filters.push({
          name: 'data_model_type',
          op: 'in',
          val: [CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER],
        });
        break;
      }
      case CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL: {
        filter.filters.push({
          name: 'data_model_type',
          op: 'in',
          val: [CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL],
        });
        break;
      }
      default:
        filter.filters.push({
          name: 'data_model_type',
          op: 'in',
          val: [CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER, CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL],
        });
        break;
    }
    fetchModels(filter);
  }, [fetchModels, selectedCode, selectedClass]);

  const removeInvalidJsonStateKeys = useCallback((row) => {
    const newRow = { ...row };
    // remove properties that contain circular references and methods
    ['allCells', 'cells', 'values'].forEach((prop) => delete newRow[prop]);
    return newRow;
  }, []);
  const handleActuarialSelectGridRow = useCallback(
    (row) => {
      selectGridRow(removeInvalidJsonStateKeys(row), 'actuarial');
    },
    [selectGridRow, removeInvalidJsonStateKeys]
  );
  const handleClassifierSelectGridRow = useCallback(
    (row) => {
      selectGridRow(removeInvalidJsonStateKeys(row), 'classifier');
    },
    [selectGridRow, removeInvalidJsonStateKeys]
  );
  const handleActuarialDeselectGridRow = useCallback(
    (row) => {
      deselectGridRow(row, 'actuarial');
    },
    [deselectGridRow]
  );
  const handleClassifierDeselectGridRow = useCallback(
    (row) => {
      deselectGridRow(row, 'classifier');
    },
    [deselectGridRow]
  );
  const handleActuarialSetGridState = useCallback(
    (state) => {
      setGridState(state, 'actuarial');
    },
    [setGridState]
  );
  const handleClassifierSetGridState = useCallback(
    (state) => {
      setGridState(state, 'classifier');
    },
    [setGridState]
  );

  const enableSelection = useCallback((row) => {
    const isActuarial = CONSTANTS.TAL_MODEL.TAGS.slice(1).includes(row.original.dataModelType);
    if (isActuarial) {
      const hasActuarialSuccess = row.original.recentStatus || false;
      const actuarialSuccessMatched =
        row.original.recentStatus === CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS;
      const actuarialMatchedName = [
        'CSAA, Auto Policy Cancellation, Client Baseline',
        'CSAA, Auto Policy Cancellation, Combined Model',
      ].includes(row.original.name);
      const isActuarialParent = row.depth === 0;

      return isActuarialParent || (hasActuarialSuccess && actuarialSuccessMatched && !actuarialMatchedName);
    }

    return true;
  }, []);

  const numberSort = useMemo(
    () => (rowA, rowB, columnId) => {
      const a = Number(rowA.values[columnId]);
      const b = Number(rowB.values[columnId]);
      return a > b ? 1 : -1;
    },
    []
  );

  const classifierData = useMemo(
    () =>
      models
        .filter((model) => model.dataModelType === CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER)
        .filter((model) => !checkHiddenByRole(siteRole || role, model.hiddenByRole))
        .filter((model) => !get(model, 'modelData.metadataPayload.shared', false))
        .map((model) => ({ ...model, selectedCode })),
    [models, siteRole, role, selectedCode]
  );
  const hasClassfierData = !!classifierData?.length;
  const classifierCols = useMemo(
    () => [
      {
        id: 'name',
        Header: (
          <Text textSize="small" p={{ l: '2rem' }} maxW="100%" data-tag="model-name">
            Model Name
          </Text>
        ),
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value }) =>
          CellText({
            value,
            'data-tip': value,
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: 'name',
        isEditable: isAdmin ? CONSTANTS.CONTROL_STATES.ENABLED : CONSTANTS.CONTROL_STATES.DISABLED,
        onEdit: (model, name) => editModel(model.id, { name, dataModelType: model.dataModelType }),
      },
      {
        Header: (
          <Div d="flex" align="center" p={{ l: '2rem' }}>
            <Icon name="Positive" size=".875rem" viewBox="0 0 50 50" color="tagHit" m={{ r: '.5em' }} />
            <Text textSize="small">HIT</Text>
          </Div>
        ),
        id: 'positiveSegment',
        disableSortBy: true,
        justify: 'left',
        cellJustify: 'left',
        style: { paddingLeft: '2rem' },
        accessor: 'modelData',
        Cell: ({ value }) => CellHit({ modelData: value }),
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'positiveMatched',
        justify: 'center',
        accessor: ({ modelData }) => modelData?.dataPayload?.positiveSegment?.matched ?? null,
        sortType: numberSort,
        Cell: CellHitMatched,
      },
      {
        Header: (
          <Div d="flex" align="center" m={{ l: '2rem' }}>
            <Icon name="Negative" size=".875rem" viewBox="0 0 50 50" color="tagMiss" m={{ r: '.5em' }} />
            <Text textSize="small">MISS</Text>
          </Div>
        ),
        id: 'negativeEngagement',
        disableSortBy: true,
        justify: 'left',
        accessor: 'modelData',
        Cell: ({ value }) => CellMiss({ modelData: value }),
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'negativeMatched',
        justify: 'center',
        accessor: ({ modelData }) => modelData?.dataPayload?.negativeSegment?.matched ?? null,
        sortType: numberSort,
        Cell: CellMissMatched,
      },
      {
        Header: (
          <Div d="flex" align="center" m={{ l: '2rem' }}>
            <Icon name="Security" size=".875rem" viewBox="0 0 10.29 12" color="tagValidation" m={{ r: '.5em' }} />
            <Text textSize="small">VALIDATION</Text>
          </Div>
        ),
        id: 'validationEngagement',
        disableSortBy: true,
        justify: 'left',
        accessor: 'modelData',
        Cell: ({ value }) => CellValidation({ modelData: value }),
      },
      {
        Header: (
          <Div justify="right">
            <Text textSize="small">Matched</Text>
          </Div>
        ),
        id: 'validationMatched',
        justify: 'center',
        accessor: ({ modelData }) => modelData?.dataPayload?.validationSegment?.matched ?? null,
        sortType: numberSort,
        Cell: CellValidationMatched,
      },
      {
        Header: (
          <Text textSize="small" data-test="risk-perc">
            Risk %
          </Text>
        ),
        id: 'petsPercentage',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: false,
        accessor: ({ modelData }) => modelData?.dataPayload?.pets_percentage ?? null,
        sortType: numberSort,
        Cell: ({ value }) => (value ? CellPercent({ value }) : '–'),
      },
      {
        id: 'tag',
        Header: (
          <Text textSize="small" p={{ l: '2rem' }}>
            Type
          </Text>
        ),
        justify: 'left',
        cellJustify: 'left',
        Cell: (value) => CellTag({ ...value, bg: 'white' }),
        accessor: ({ dataModelType, predictionType, typeDetail }) => ({ dataModelType, predictionType, typeDetail }),
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        id: 'actions',
        justify: 'left',
        disableSortBy: true,
        // eslint-disable-next-line react/prop-types
        accessor: ({ id, recentStatus, hiddenByRole }) =>
          CellActions({
            id,
            siteRole,
            recentStatus,
            hiddenByRole,
            setModelToHideId,
            setModelToDeleteId,
            setModelToApproveId,
            setHideModalOpen,
            setDeleteModalOpen,
            checkDeleteModel,
            setApproveModalOpen,
          }),
      },
      {
        Header: <Text textSize="small">Created</Text>,
        id: 'created',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Status</Text>,
        id: 'status',
        cellJustify: 'center',
        justify: 'center',
        disableSortBy: false,
        accessor: ({ recentStatus }) => getStatusGroup(recentStatus),
        Cell: CellStatus,
      },
    ],
    [isAdmin, numberSort, editModel, siteRole, checkDeleteModel, getStatusGroup]
  );

  const actuarialData = useMemo(() => {
    const actuarialModels = models
      .filter((model) => model.dataModelType === CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL)
      .filter((model) => !checkHiddenByRole(siteRole || role, model.hiddenByRole))
      .filter((model) => !get(model, 'modelData.metadataPayload.shared', false));
    return actuarialModels.map((model) => {
      const subRows = model.modelGroup
        .map((subModel) => ({
          ...subModel,
          selectedCode,
          modelData: {
            ...subModel.modelData,
            dataPayload: {
              ...subModel.modelData.dataPayload,
              actuarialSegment: model.modelData.dataPayload.actuarialSegment,
            },
          },
        }))
        .filter((elem) => !checkHiddenByRole(siteRole || role, elem.hiddenByRole) && !elem.isDeleted)
        .filter((elem) => !get(elem, 'modelData.metadataPayload.shared', false));
      subRows.sort((a, b) => dayjs.utc(a.createdAt) < dayjs.utc(b.createdAt));
      return {
        ...model,
        selectedCode,
        subRows,
      };
    });
  }, [models, siteRole, role, selectedCode]);
  const hasActuarialData = !!actuarialData?.length;
  const actuarialCols = useMemo(
    () => [
      ExpanderColumn,
      {
        id: 'name',
        Header: (info) => {
          const expanded = checkAllTablesRowsExpanded(info);
          if (expanded !== rowsOpen) setRowsOpen(expanded);
          return CellText({ value: 'Model Name', textSize: 'small', textWeight: '700', p: { l: '2rem' } });
        },
        justify: 'left',
        cellJustify: 'left',
        Cell: (info) => {
          const { value } = info;
          return CellText({
            value,
            'data-tip': value,
            style: { overflow: 'visible', whiteSpace: 'nowrap' },
            maxW: '14rem',
            minW: '10rem',
          });
        },
        accessor: 'name',
        isEditable: isAdmin ? CONSTANTS.CONTROL_STATES.ENABLED : CONSTANTS.CONTROL_STATES.DISABLED,
        onEdit: (model, name) => editModel(model.id, { name, dataModelType: model.dataModelType }),
      },
      {
        Header: (
          <Text textSize="small" data-test="0-claims">
            0 Claims Matched
          </Text>
        ),
        id: 'no_claims',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: false,
        sortType: numberSort,
        accessor: noClaimsAccessor,
        Cell: CellNumber,
      },
      {
        Header: (
          <Text textSize="small" data-test="gte-1-claim">
            ≥1 Claims Matched
          </Text>
        ),
        id: 'some_claims',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: false,
        sortType: numberSort,
        accessor: someClaimsAccessor,
        Cell: CellNumber,
      },
      {
        Header: <Text textSize="small">Expected</Text>,
        id: 'expected_risk',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: expectedRiskAccessor,
        Cell: CellPercent,
      },
      {
        Header: <Text textSize="small">Onboarded</Text>,
        id: 'onboarded_risk',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: onboardedRiskAccessor,
        Cell: CellPercent,
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'matched_risk',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: matchedRiskAccessor,
        Cell: CellPercent,
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'matched_validation',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: matchedValAccessor,
        Cell: CellNumber,
      },
      {
        Header: (
          <Text textSize="small" data-test="matched_perc">
            Percentage
          </Text>
        ),
        id: 'matched_validation_perc',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: matchValPercAccessor,
        Cell: CellPercent,
      },
      {
        id: 'tag',
        Header: (
          <Text textSize="small" p={{ l: '2rem' }}>
            Type
          </Text>
        ),
        justify: 'left',
        cellJustify: 'left',
        Cell: (value) => CellTag({ ...value, bg: 'white' }),
        accessor: ({ dataModelType, typeDetail, predictionType }) => ({ dataModelType, typeDetail, predictionType }),
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        id: 'actions',
        justify: 'left',
        disableSortBy: true,
        // eslint-disable-next-line react/prop-types
        accessor: actActionsAccessor,
        Cell: ({ value }) =>
          value
            ? ActuarialCellActions({
                ...JSON.parse(value),
                role,
                siteRole,
                originalTenant,
                setModelToDeleteId,
                setModelToDeleteType,
                setModelToApproveId,
                setModelToHideId,
                setDeleteModalOpen,
                checkDeleteModel,
                setApproveModalOpen,
                setHideModalOpen,
                fetchEntityDl,
              })
            : null,
      },
      {
        Header: <Text textSize="small">Created</Text>,
        id: 'created',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Status</Text>,
        id: 'status',
        cellJustify: 'center',
        justify: 'center',
        disableSortBy: false,
        accessor: ({ recentStatus }) => getStatusGroup(recentStatus),
        Cell: CellStatus,
      },
    ],
    [
      isAdmin,
      numberSort,
      rowsOpen,
      editModel,
      role,
      siteRole,
      originalTenant,
      checkDeleteModel,
      fetchEntityDl,
      getStatusGroup,
    ]
  );

  const handleFilterButtonClick = useCallback(
    (filterType) => {
      deselectAllModelIds();
      if (selectedCode === filterType) return setSelectedCode('');
      return setSelectedCode(filterType);
    },
    [deselectAllModelIds, selectedCode, setSelectedCode]
  );

  useEffect(() => {
    if (approveModelStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Approving the model failed. Please try again');
    }
    if (approveModelStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Model successfully approved', { autoClose: true });
    }
  }, [approveModelStatus, enqueueNotification]);

  useEffect(() => {
    fetchClientByTenant(tenant);
    const hasState = ![null, undefined].includes(selectedCode);
    const hasClass = ![null, undefined].includes(selectedClass);
    const query = `?filter=${selectedCode}&modelClass=${selectedClass}`;
    if (hasState || hasClass) {
      getData();
      history.replace({
        pathname: '/models',
        search: query,
        state: {},
      });
    }
  }, [tenant, selectedCode, selectedClass, fetchClientByTenant, history, getData, unset]);

  useEffect(() => {
    deselectAllModelIds();
  }, [tenant, deselectAllModelIds]);

  useEffect(() => {
    const badLength = selectedModelIds.length !== 1;
    const hasClassifiers = get(selectedGridRows, 'classifier').length;
    const hasClassifierSuccess = get(selectedGridRows, 'classifier[0].original.recentStatus') || false;
    const classifierSuccessMatched =
      get(selectedGridRows, 'classifier[0].original.recentStatus') ===
      CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS;
    const classifierMatchedName = [
      'CSAA, Auto Policy Cancellation, Client Baseline',
      'CSAA, Auto Policy Cancellation, Combined Model',
    ].includes(get(selectedGridRows, 'classifier[0].original.name'));
    const hasActuarials = get(selectedGridRows, 'actuarial').length;
    const hasActuarialSuccess = get(selectedGridRows, 'actuarial[0].original.recentStatus') || false;
    const actuarialSuccessMatched =
      get(selectedGridRows, 'actuarial[0].original.recentStatus') ===
      CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS;
    const actuarialMatchedName = [
      'CSAA, Auto Policy Cancellation, Client Baseline',
      'CSAA, Auto Policy Cancellation, Combined Model',
    ].includes(get(selectedGridRows, 'actuarial[0].original.name'));
    const isActuarialParent = hasActuarials && get(selectedGridRows, 'actuarial[0].depth') === 0;

    setDisableModelResults(
      badLength ||
        (hasClassifiers && hasClassifierSuccess && (!classifierSuccessMatched || classifierMatchedName)) ||
        isActuarialParent ||
        (hasActuarials && hasActuarialSuccess && (!actuarialSuccessMatched || actuarialMatchedName))
    );
    setDisableInputDistributions(badLength || hasClassifiers || !isActuarialParent);
  }, [selectedModelIds.length, selectedGridRows]);

  useEffect(() => {
    setIsAdmin(role === CONSTANTS.ROLE.SUPER_ADMIN || role === CONSTANTS.ROLE.ADMIN);
  }, [role]);

  useEffect(() => {
    if (fetchModelDownloadStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Download failed. Please try again later');
    }
    if (fetchModelDownloadStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Download successfully completed', {
        autoClose: true,
      });
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl, fetchModelDownloadStatus, enqueueNotification]);
  return (
    <Div flexDir="column" d="flex" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="6">
          <Div data-test="inp-dst-div">
            <Button
              buttonStyle="primary"
              data-tip={hasActuarialData ? disabledInputDistStr : 'Actuarial model must be requested first'}
              disabled={disableInputDistributions}
              onClick={() => {
                history.push(
                  ROUTES.MODEL_INPUT_DISTRIBUTIONS.replace(':modelId', selectedModelIds[0]).replace(
                    ':modelClass',
                    selectedModelClasses[0]
                  )
                );
              }}
            >
              <Icon
                name="DistributionChartEnclosed"
                color="white"
                m={{ r: '0.5rem' }}
                size="16px"
                viewBox="0 0 16 16"
              />
              INPUT DISTRIBUTIONS
            </Button>
          </Div>
          {get(selectedGridRows, 'classifier').length > 1 && (
            <Button
              buttonStyle="primary"
              disabled={
                get(selectedGridRows, 'classifier').length < selectedModelIds.length ||
                get(selectedGridRows, 'classifier').length <= 1 ||
                !validModels(get(selectedGridRows, 'classifier').map((model) => model.original.id))
              }
              onClick={() => {
                const query = get(selectedGridRows, 'classifier')
                  .map((model) => `id=${model.original.id}`)
                  .join('&');
                deselectAllModelIds();
                history.push(`${ROUTES.MODELS_COMPARE}?${query}`.replace(':modelClass', 'classifier'));
              }}
            >
              <Icon name="Thinkalike" color="white" m={{ r: '0.5rem' }} size="1rem" />
              COMPARE LIFT CHARTS
            </Button>
          )}
          {get(selectedGridRows, 'actuarial').length > 1 && (
            <Button
              buttonStyle="primary"
              disabled={
                get(selectedGridRows, 'actuarial').length < selectedModelIds.length ||
                get(selectedGridRows, 'actuarial').length <= 1 ||
                !validModels(get(selectedGridRows, 'actuarial').map((model) => model.original.id))
              }
              onClick={() => {
                const query = get(selectedGridRows, 'actuarial')
                  .map((model) => `id=${model.original.id}`)
                  .join('&');
                deselectAllModelIds();
                history.push(`${ROUTES.MODELS_COMPARE}?${query}`.replace(':modelClass', 'actuarial'));
              }}
            >
              <Icon name="Thinkalike" color="white" m={{ r: '0.5rem' }} size="1rem" />
              COMPARE LIFT CHARTS
            </Button>
          )}
          {(([0, 1].includes(get(selectedGridRows, 'classifier').length) &&
            get(selectedGridRows, 'actuarial').length === 0) ||
            ([0, 1].includes(get(selectedGridRows, 'actuarial').length) &&
              get(selectedGridRows, 'classifier').length === 0)) && (
            <Div data-test="lift-charts-btn">
              <Button
                buttonStyle="primary"
                data-tip={hasActuarialData || hasClassfierData ? disabledLiftChartsStr : 'Complete a model first'}
                disabled={disableModelResults}
                onClick={() => {
                  history.push(
                    ROUTES.MODEL_LIFT_CHARTS.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }}
              >
                <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.5rem' }} />
                LIFT CHARTS
              </Button>
            </Div>
          )}
          {(([0, 1].includes(get(selectedGridRows, 'classifier').length) &&
            get(selectedGridRows, 'actuarial').length === 0) ||
            ([0, 1].includes(get(selectedGridRows, 'actuarial').length) &&
              get(selectedGridRows, 'classifier').length === 0)) && (
            <Div data-test="roi-calculator-btn">
              <Button
                buttonStyle="primary"
                data-tip={hasActuarialData || hasClassfierData ? disabledRoiCalculatorStr : 'Complete a model first'}
                disabled={disableModelResults}
                onClick={() => {
                  history.push(
                    ROUTES.MODEL_ROI_CALCULATOR.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }}
              >
                <Icon name="Roi" viewBox="0 0 16 16" color="white" m={{ r: '0.5rem' }} size="1rem" />
                ROI CALCULATOR
              </Button>
            </Div>
          )}
          {(([0, 1].includes(get(selectedGridRows, 'classifier').length) &&
            get(selectedGridRows, 'actuarial').length === 0) ||
            ([0, 1].includes(get(selectedGridRows, 'actuarial').length) &&
              get(selectedGridRows, 'classifier').length === 0)) && (
            <Div data-test="validation-analytics-btn">
              <Button
                buttonStyle="primary"
                data-tip={
                  hasActuarialData || hasClassfierData ? disabledValidationAnalyticsStr : 'Complete a model first'
                }
                disabled={disableModelResults}
                onClick={() => {
                  history.push(
                    ROUTES.MODEL_VALIDATION_ANALYTICS.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }}
              >
                <Icon name="Customer" color="white" m={{ r: '0.5rem' }} size="1rem" />
                VALIDATION ANALYTICS
              </Button>
            </Div>
          )}
          {email?.includes('@pinpoint.ai') && (
            <Button
              buttonStyle="admin"
              data-tip={
                !hasActuarialData && !hasClassfierData
                  ? 'Client must request a model first'
                  : 'View requirements prior to model approval'
              }
              data-test="aprv-cl-btn"
              disabled={!hasActuarialData && !hasClassfierData}
            >
              <Icon name="Checklist" color="white" m={{ r: '0.5rem' }} viewBox="0 0 24 24" size="1.25rem" />
              APPROVAL CHECKLIST
            </Button>
          )}
        </Col>
        <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0" data-test="filter-btns">
          <Icon name="Filter" viewBox="0 0 17 16" size="1rem" m={{ x: '.625rem' }} data-tip="View filter" />
          <Button
            buttonStyle="tab"
            data-tip="View Actuarial models only"
            selected={selectedClass === CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL}
            onClick={() => {
              if (selectedClass !== CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL) {
                unset('models');
                deselectAllModelIds();
                setSelectedClass(CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL);
              }
            }}
          >
            <Icon name="ActuarialModel" size="1rem" viewBox="0 0 12 12" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">ACTUARIAL</Text>
          </Button>
          <Button
            buttonStyle="tab"
            data-tip="View Classifier models only"
            selected={selectedClass === CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER}
            onClick={() => {
              if (selectedClass !== CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER) {
                unset('models');
                deselectAllModelIds();
                setSelectedClass(CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER);
              }
            }}
          >
            <Icon name="ClassifierModel" size="1rem" viewBox="0 0 10.46 16" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">CLASSIFIER</Text>
          </Button>
          <Button
            buttonStyle="tab"
            m={{ r: '0rem' }}
            data-tip="View all model types"
            selected={['', CONSTANTS.TAL_MODEL.MODEL_CLASS.ALL].includes(selectedClass)}
            onClick={() => {
              if (!['', CONSTANTS.TAL_MODEL.MODEL_CLASS.ALL].includes(selectedClass)) {
                unset('models');
                deselectAllModelIds();
                setSelectedClass(CONSTANTS.TAL_MODEL.MODEL_CLASS.ALL);
              }
            }}
          >
            <Text textSize="body">ALL</Text>
          </Button>
        </Col>
      </Row>

      {fetchModelsStatus === CONSTANTS.API.STARTED && !hasActuarialData && !hasClassfierData && <Message />}

      {!hasActuarialData && !hasClassfierData && (
        <Row data-debug="actuarial-row" m={{ x: '1.5rem', b: '.5rem' }}>
          <Col size="12" rounded="md" bg="white" gutterWidth="0">
            <Row data-debug="filter-row" align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap">
              <Col size="12" textAlign="right" data-test="filter-status-btns">
                <StatusFilterButtonGroup
                  selectedCode={selectedCode}
                  apiStatus={fetchModelsStatus}
                  onFilterButtonClick={handleFilterButtonClick}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {hasActuarialData && (
        <Row data-debug="actuarial-row" m={{ x: '1.5rem', b: '.5rem' }}>
          <Col size="12" rounded="md" bg="white" gutterWidth="0">
            {hasActuarialData && (
              <Row data-debug="filter-row" align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap">
                <Col size="4" gutterWidth="1.5rem">
                  <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                    Actuarial Loss Models
                  </Text>
                </Col>
                <Col size="8" textAlign="right" data-test="filter-status-btns">
                  <StatusFilterButtonGroup
                    selectedCode={selectedCode}
                    apiStatus={fetchModelsStatus}
                    onFilterButtonClick={handleFilterButtonClick}
                  />
                </Col>
              </Row>
            )}
            <Row data-debug="actuarial-table-row" align="center" m={{ t: '1rem', b: '.75rem' }}>
              <Col m={{ y: '0rem' }} size="12">
                <Col
                  bg="shadeF2"
                  gutterWidth="0"
                  h="calc(1.75rem + 3px)"
                  style={{
                    marginBottom: '-2px',
                  }}
                  rounded={{ tr: 'sm', tl: 'sm' }}
                >
                  <Col
                    d="grid"
                    gutterWidth="0"
                    style={{
                      gridTemplateColumns: actuarialOverheaderWidths,
                    }}
                  >
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div
                      border={{ b: '1px dashed' }}
                      borderColor="shadeAF"
                      className="column"
                      textAlign="center"
                      m={{ x: '.5rem' }}
                      h="1.75rem"
                      // bg="blueLight"
                    >
                      <Text
                        data-test="dataset-claim-freq"
                        textWeight="700"
                        textSize="small"
                        p={{ t: '.625rem', b: '.5rem' }}
                      >
                        DATASET CLAIMANT FREQUENCY
                      </Text>
                    </Div>
                    <Div
                      border={{ b: '1px dashed' }}
                      borderColor="shadeAF"
                      className="column"
                      textAlign="center"
                      h="1.75rem"
                      m={{ x: '.5rem' }}
                      // bg="greenLight"
                    >
                      <Text textWeight="700" textSize="small" p={{ t: '.625rem', b: '.5rem' }}>
                        VALIDATION RECORDS
                      </Text>
                    </Div>
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                  </Col>
                </Col>
              </Col>
              <Col size="12">
                <ViewModelFeatureToggle
                  tenant={tenant}
                  hasData={hasActuarialData}
                  getData={getData}
                  columns={actuarialCols}
                  data={actuarialData}
                  gridState={gridState.actuarial}
                  controlledGridState={controlledGridState.actuarial}
                  rawData={models}
                  onRowSelect={handleActuarialSelectGridRow}
                  onRowDeselect={handleActuarialDeselectGridRow}
                  onRowExpand={setRowsOpen}
                  enableSelection={enableSelection}
                  selectionTooltip={() => 'Select'}
                  hasSelection
                  hasExpansion
                  maxSelection={maxGridRowSelection.actuarial}
                  apiStatus={fetchModelsStatus}
                  setGridState={handleActuarialSetGridState}
                  subRowsColsIndentIndexes={[1, 2, 3]}
                  widths={actuarialColWidths}
                  DivProps={{
                    m: '0rem',
                    p: '0rem',
                  }}
                  TableProps={{
                    p: { y: '0rem' },
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {hasClassfierData && (
        <Row data-debug="classifier-row" m={{ x: '1.5rem', t: hasActuarialData ? '.5rem' : '0rem' }}>
          <Col size="12" rounded="md" bg="white" gutterWidth="0">
            {!hasActuarialData && (
              <Row align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap" data-test="classifier-title-row">
                <Col data-test="classifier-title" size="4" gutterWidth="1.5rem">
                  <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                    Classifier Models
                  </Text>
                </Col>
                <Col size="8" textAlign="right" data-test="filter-status-btns">
                  <StatusFilterButtonGroup
                    selectedCode={selectedCode}
                    apiStatus={fetchModelsStatus}
                    onFilterButtonClick={handleFilterButtonClick}
                  />
                </Col>
              </Row>
            )}
            {hasActuarialData && (
              <Row align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap" data-test="classifier-title-row">
                <Col size="12" gutterWidth="1.5rem">
                  <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                    Classifier Models
                  </Text>
                </Col>
              </Row>
            )}
            <Row data-debug="classifier-table-row" flexGrow="1" align="center" m={{ t: '1rem', b: '.75rem' }}>
              <Col size="12">
                <ViewModelFeatureToggle
                  tenant={tenant}
                  hasData={hasClassfierData}
                  getData={getData}
                  columns={classifierCols}
                  data={classifierData}
                  gridState={gridState.classifier}
                  controlledGridState={controlledGridState.classifier}
                  rawData={models}
                  onRowSelect={handleClassifierSelectGridRow}
                  onRowDeselect={handleClassifierDeselectGridRow}
                  hasSelection
                  selectionTooltip={() => 'Select'}
                  hasExpansion={false}
                  maxSelection={maxGridRowSelection.classifier}
                  apiStatus={fetchModelsStatus}
                  setGridState={handleClassifierSetGridState}
                  widths={classifierColWidths}
                  DivProps={{
                    m: '0rem',
                    p: '0rem',
                  }}
                  TableProps={{
                    p: { y: '0rem' },
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <HideModelDialog
        isOpen={hideModalOpen}
        model={modelListFlat.find((model) => model.id === modelToHideId)}
        actions={{
          cancel: { on: () => {} },
          close: {
            on: () => {
              setHideModalOpen(false);
              setModelToHideId(null);
            },
          },
          handlers: [{ on: handleHideModel, after: getData }],
        }}
      />
      <DeleteModelDialog
        isOpen={deleteModalOpen}
        dependentResources={checkDeleteModelDependents}
        dataModelType={modelToDeleteType}
        actions={{
          cancel: { on: () => {} },
          close: {
            on: () => {
              setDeleteModalOpen(false);
              setModelToDeleteId(null);
              setModelToDeleteType(null);
              window.setTimeout(() => unset('checkDeleteModelDependents'), 500);
            },
          },
          handlers: [{ on: handleDeleteModel, after: getData }],
        }}
      />
      <ApproveModelDialog
        isOpen={approveModalOpen}
        actions={{
          cancel: { on: () => {} },
          close: {
            on: () => {
              setApproveModalOpen(false);
              setModelToApproveId(null);
            },
          },
          handlers: [{ on: handleApproveModel, after: getData }],
        }}
      />
    </Div>
  );
}

const mapStateToProps = ({ app, modelsList }) => ({ app, modelsList });

const mapDispatchToProps = (dispatch) => ({
  fetchModels: (...params) => dispatch(modelsListActions.fetchModels(...params)),
  selectGridRow: (row, type) => dispatch(modelsListActions.selectGridRow(row, type)),
  deselectGridRow: (row, type) => dispatch(modelsListActions.deselectGridRow(row, type)),
  deselectAllModelIds: (...params) => dispatch(modelsListActions.deselectAllModelIds(...params)),
  setGridState: (state, type) => dispatch(modelsListActions.setGridState(state, type)),
  editModel: (...params) => dispatch(modelsListActions.editModel(...params)),
  hideModel: (...params) => dispatch(modelsListActions.hideModel(...params)),
  showModel: (...params) => dispatch(modelsListActions.showModel(...params)),
  deleteModel: (...params) => dispatch(modelsListActions.deleteModel(...params)),
  checkDeleteModel: (...params) => dispatch(modelsListActions.checkDeleteModel(...params)),
  unset: (key) => dispatch(modelsListActions.unset(key)),
  approveModel: (...params) => dispatch(modelsListActions.approveModel(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  fetchClientByTenant: (...params) => dispatch(modelsListActions.fetchClientByTenant(...params)),
  fetchEntityDl: (...params) => dispatch(modelsListActions.fetchModelDownloadFromEntity(...params)),
});

Models.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      siteRole: PropTypes.oneOf([PropTypes.string, null]).isRequired,
      email: PropTypes.string.isRequired,
    }),
  }).isRequired,
  modelsList: PropTypes.shape({
    checkDeleteModelDependents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    models: PropTypes.arrayOf(PropTypes.shape({})),
    fetchModelsStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchModelDownloadStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    downloadUrl: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    selectedModelIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    gridState: PropTypes.shape({
      classifier: PropTypes.shape({}).isRequired,
      actuarial: PropTypes.shape({}).isRequired,
    }).isRequired,
    controlledGridState: PropTypes.shape({
      classifier: PropTypes.shape({}).isRequired,
      actuarial: PropTypes.shape({}).isRequired,
    }).isRequired,
    maxGridRowSelection: PropTypes.shape({
      actuarial: PropTypes.number.isRequired,
      classifier: PropTypes.number.isRequired,
    }).isRequired,
    selectedGridRows: PropTypes.shape({
      classifier: PropTypes.shape({}).isRequired,
      actuarial: PropTypes.shape({}).isRequired,
    }).isRequired,
    approveModelStatus: PropTypes.string.isRequired,
    client: PropTypes.shape({}),
  }).isRequired,
  fetchModels: PropTypes.func.isRequired,
  selectGridRow: PropTypes.func.isRequired,
  deselectGridRow: PropTypes.func.isRequired,
  deselectAllModelIds: PropTypes.func.isRequired,
  setGridState: PropTypes.func.isRequired,
  editModel: PropTypes.func.isRequired,
  showModel: PropTypes.func.isRequired,
  hideModel: PropTypes.func.isRequired,
  deleteModel: PropTypes.func.isRequired,
  checkDeleteModel: PropTypes.func.isRequired,
  approveModel: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  fetchClientByTenant: PropTypes.func.isRequired,
  fetchEntityDl: PropTypes.func.isRequired,
  unset: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Models));
